import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { routeInfo, userCredentials } from 'recoilState/GlobalState';

import s from "styles/pages/Pages.module.scss"
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal, ViewModal } from "./Actions/index";
import { Buttons, HeaderButton } from "components";
import { ACTIONS } from "consts/Actions";
import { Add, Cached, EditOutlined, DeleteOutlineRounded, VisibilityRounded } from '@mui/icons-material';
import { useService, puedeEditarServicio } from 'components/route/ServiceRoute';
import { hospitalesTitles as titles } from "consts/titulos-de-tablas";
import XGrid from 'components/UI/Grid/XGrid';
import { RNHEstablecimientoGet } from 'services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet';
import { nombresDeNivelPorCod } from '../niveles/catalogo';

/**
 * Cabecera, del caracter que guarda el back al texto que se muestra.
 *
 * `A` y `Z` son las unicas que se pueden elegir hoy; `B` sigue aca porque los registros
 * cargados antes del cambio la tienen guardada y hay que poder leerlos.
 */
const CABECERA = {
  A: "Área",
  Z: "Zona y Área",
  // Del esquema viejo, para leer lo cargado antes.
  B: "Zona y Área",
  N: "Ninguna",
};

export default function HospitalesSalud() {
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [hopitales, setHospitales] = useState([]);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} })
  // eslint-disable-next-line no-unused-vars
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // DESESTRUCTURADO: `useService()` devuelve el contexto del Outlet, que es
  // `{ service }`. Sin las llaves, `service` era ese envoltorio y cualquier campo
  // que se le pidiera daba undefined —incluido `servicioPermiso`, que dejaba los
  // botones de ABM apagados para todos los roles—.
  const { service } = useService()
  const credentials = useRecoilValue(userCredentials);

  const getHospitalesData = async (reset) => {
    setRowSelected({ status: false, row: {} })
    setPage(0)
    setHospitales([]);
    setIsLoading(true);
    // El catalogo de niveles va en la misma tanda: el establecimiento trae el COD del
    // nivel pero no su nombre, y las filas se arman aca abajo. Pidiendolos juntos el
    // nombre ya esta cuando hace falta, sin carrera ni un segundo render.
    const [{ data: result }, nombreDeNivel] = await Promise.all([
      RNHEstablecimientoGet(credentials, { tipo: "H" }),
      nombresDeNivelPorCod(credentials),
    ])
    const newRows = result[0]?.rnhEstablecimientoId ? result.map((item) => {
      return {
        id: item.rnhEstablecimientoId,
        name: item.rnhEstablecimientoNombre,
        cod: item.rnhEstablecimientoCod,
        // La grilla muestra el NOMBRE; el cod queda abajo, en los datos que usa el
        // formulario. Si el catalogo no lo tiene, el cod es mejor que un vacio.
        nivel:
          nombreDeNivel[item.rnhEstablecimientoNivelCod] ??
          item.rnhEstablecimientoNivelCod,
        areaId: item.rnhEstablecimientoAreaId,
        area: item.rnhEstablecimientoAreaNombre,
        zonaId: item.rnhEstablecimientoZonaId,
        zona: item.rnhEstablecimientoZonaNombre,
        // Mismas palabras que el select del formulario. `B` y `N` ya no se pueden
        // elegir, pero se siguen traduciendo para que los registros viejos se lean.
        cabecera: CABECERA[item.rnhEstablecimientoCabecera] ?? "Ninguna",
        localidad: item.localidadNombre,
        // No son columnas: viajan en la fila para que Modificar, Eliminar y Consultar no
        // tengan que volver a pedir el registro. La response ya los trae todos.
        //
        // `cabeceraCod` va aparte de `cabecera`: la grilla muestra el texto y el
        // formulario necesita el caracter que guarda el back.
        version: item.rnhEstablecimientoVersion,
        cabeceraCod: item.rnhEstablecimientoCabecera ?? "",
        // El cod del nivel, que es con lo que Modificar resuelve el select. Va aparte del
        // nombre por lo mismo que `cabeceraCod` va aparte de `cabecera`.
        nivelCod: item.rnhEstablecimientoNivelCod ?? "",
        calle: item.rnhEstablecimientoCalle ?? "",
        numero: item.rnhEstablecimientoNumero ?? "",
        cpa: item.rnhEstablecimientoCPA ?? "",
        localidadId: item.rnhEstablecimientoLocalidadId ?? "",
        lat: item.rnhEstablecimientoLatitud,
        lng: item.rnhEstablecimientoLongitud,
      };
    }) : [];
    setHospitales(newRows);
    setIsLoading(false);
    return {
      rows: newRows,
      rowCount: newRows.length,
    };
  }
  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };
  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };
  useEffect(() => {
    getHospitalesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.servicioNombre])

  useLayoutEffect(() => {
    setRoute({
      wordKey: `Hospitales - Programa Salud RN`
    })
  }, [service.servicioNombre, setRoute])

  return (
    <main className={s.main}>
      {/* El alta, la modificacion y la consulta abren con size="xl" (1200px) y no con la "lg"
          de las otras cuatro entidades: el hospital suma nivel, zona, area y cabecera al
          domicilio, y con ese ancho la localidad y el codigo postal entran en la misma fila. */}
      <Modal big={modalDetails.procede === ACTIONS.INSERT || modalDetails.procede === ACTIONS.UPDATE || modalDetails.procede === ACTIONS.HISTORY || modalDetails.procede === ACTIONS.VIEW} size={modalDetails.procede === ACTIONS.DELETE ? "sm" : modalDetails.procede === ACTIONS.INSERT || modalDetails.procede === ACTIONS.UPDATE || modalDetails.procede === ACTIONS.VIEW ? "xl" : ""}  compact={modalDetails.procede === ACTIONS.INSERT || modalDetails.procede === ACTIONS.UPDATE || modalDetails.procede === ACTIONS.DETAIL || modalDetails.procede === ACTIONS.VIEW} open={modalDetails.isOpen} title={modalDetails.title} onClose={closeModal}>
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            row={rowSelected.row}
            updateList={getHospitalesData}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            row={rowSelected.row}
            servicio={service}
            updateList={getHospitalesData}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.VIEW && (
          <ViewModal
            row={rowSelected.row}
            servicio={service}
            updateList={getHospitalesData}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            servicio={service}
            updateList={getHospitalesData}
            closeModal={closeModal}
          />
        )}
      </Modal>
      <div className={s["filters-actions-right"]}>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => { getHospitalesData() }}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Detalle del Hospital"}
            handleClick={handleClick}
            data={{
              title: `${rowSelected.row.name} - Detalle `,
              procede: ACTIONS.VIEW
            }}
            disabled={!rowSelected.row.id}
          >
            <VisibilityRounded />
          </HeaderButton>
          {/* <HeaderButton
                        text={"Departamentos"}
                        handleClick={handleClick}
                        data={{
                            title: `Departamentos - ${rowSelected.row.name}`,
                            procede: ACTIONS.DETAIL
                        }}
                        disabled={!rowSelected.row.id}
                    >
                        <AccountTree />
                    </HeaderButton> */}
          <HeaderButton
            text={"Nuevo Hospital"}
            handleClick={handleClick}
            data={{
              title: "Nuevo Hospital",
              procede: ACTIONS.INSERT
            }}
            disabled={!puedeEditarServicio(service) || (rowSelected.row.id)}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar Hospital"}
            handleClick={handleClick}
            data={{
              title: "Modificar Hospital",
              procede: ACTIONS.UPDATE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <EditOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar Hospital"}
            handleClick={handleClick}
            data={{
              title: "Eliminar Hospital",
              procede: ACTIONS.DELETE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <DeleteOutlineRounded />
          </HeaderButton>
        </Buttons>
      </div>

      <div className={s.container}>
        <XGrid
          rows={hopitales}
          titles={titles}
          loading={isLoading}
          updateList={getHospitalesData}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
          page={page}
          setPage={setPage}
        />
      </div>
    </main>
  )
}
